import nanoid from 'nanoid';
import { isFunction, get, remove, head, isEmpty, map, sortedLastIndexBy } from 'lodash';

class PriorityQueue {
  constructor(comparator, keyGenerator) {
    this.queue = [];
    this.items = {};
    this.comparator = isFunction(comparator) ? (key) => comparator(this.items[key]) : undefined;
    this.keyGenerator = isFunction(keyGenerator) ? keyGenerator : (_) => nanoid();
  }

  enqueue(item) {
    const key = this.keyGenerator(item);
    const isItemExistInQueue = !!this.items[key];

    if (isItemExistInQueue) {
      console.warn(`Key already exists in the PriorityQueue: ${key}`);
      this.dequeue(key);
    }

    this.items[key] = item;

    if (this.comparator) {
      const index = sortedLastIndexBy(this.queue, key, this.comparator);
      this.queue.splice(index, 0, key);
    } else {
      this.queue.push(key);
    }
  }

  dequeue(key) {
    const removedKey = key ? head(remove(this.queue, (queuedKey) => queuedKey === key)) : this.queue.shift();

    const item = this.items[removedKey];
    delete this.items[removedKey];

    return item;
  }

  removeBy(predicate) {
    const keysToRemove = remove(this.queue, (key) => predicate(this.items[key]));
    const removedItems = map(keysToRemove, (key) => {
      const item = this.items[key];
      delete this.items[key];
      return item;
    });

    return removedItems;
  }

  getKeys() {
    return this.queue;
  }

  getQueue() {
    return map(this.queue, (key) => this.items[key]);
  }

  peek() {
    return get(this.items, head(this.queue));
  }

  isEmpty() {
    const isQueueEmpty = isEmpty(this.queue);
    const isItemsEmpty = isEmpty(this.items);

    if (isQueueEmpty !== isItemsEmpty) {
      throw new Error('Queue and Map Items are out of sync');
    }

    return isQueueEmpty && isItemsEmpty;
  }
}

export default PriorityQueue;
